// Frontend/src/lib/announcementStyles.ts
//
// Announcement priority, on the same scale as everything else.
//
// The announcements page had four of its own colour helpers — getPriorityRail,
// getPriorityColor, getPriorityBg, getPriorityIcon — that put MEDIUM on a blue
// chip while the issues page put MEDIUM on amber, and LOW on green where issues
// used slate. Same word, same four levels, two different meanings depending on
// which tab you were looking at.
//
// These are issueStyles' tones, keyed by the announcement enum. The scale is
// the product's, not any one feature's.

import { AlertOctagon, ArrowUp, Flame, Minus, type LucideIcon } from "lucide-react";

import { PRIORITY_TONE, type Tone } from "@/lib/issueStyles";
import type { AnnouncementPriority } from "@/types/announcement";

export const ANNOUNCEMENT_PRIORITY_TONE: Record<AnnouncementPriority, Tone> = {
  urgent: PRIORITY_TONE.URGENT,
  high: PRIORITY_TONE.HIGH,
  medium: PRIORITY_TONE.MEDIUM,
  low: PRIORITY_TONE.LOW,
};

export const ANNOUNCEMENT_PRIORITY_ICON: Record<AnnouncementPriority, LucideIcon> = {
  urgent: AlertOctagon,
  high: Flame,
  medium: ArrowUp,
  low: Minus,
};

/** URGENT is the only priority allowed a filled chip — see issueStyles. */
export function announcementPriorityIsLoud(priority: AnnouncementPriority): boolean {
  return priority === "urgent";
}

/**
 * Where an announcement is in its life, which is not the same question as its
 * priority. An admin scanning history wants to know what residents can see
 * right now; `is_active` alone cannot distinguish "not yet" from "no longer".
 */
export type AnnouncementState = "scheduled" | "live" | "expired";

export function announcementState(a: {
  published_at: string;
  expires_at: string | null;
}): AnnouncementState {
  const now = Date.now();
  if (new Date(a.published_at).getTime() > now) return "scheduled";
  if (a.expires_at && new Date(a.expires_at).getTime() <= now) return "expired";
  return "live";
}

export const ANNOUNCEMENT_STATE_TONE: Record<AnnouncementState, Tone> = {
  live: { rail: "#27AE60", bg: "#DCF5E6", fg: "#12693C", ring: "#B4E4C7" },
  scheduled: { rail: "#1F3A93", bg: "#E7EBF8", fg: "#1B3480", ring: "#C3CDEC" },
  expired: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
};
